/**
 * Simple Footer
 * 
 * Single row footer with minimal information.
 * Clean and unobtrusive.
 */

import { Link } from "react-router-dom";
import { 
  Facebook, 
  Instagram, 
  Twitter, 
  Youtube,
  Linkedin,
} from "lucide-react";
import { siteConfig, getEnabledSocialLinks, type SocialPlatform } from "@/lib/site-config";
import { useLayout } from "@/lib/layout-provider";

const socialIcons: Record<SocialPlatform, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
};

const FooterSimple = () => {
  const currentYear = new Date().getFullYear();
  const { currentLayout } = useLayout();
  const footerConfig = currentLayout.footer;
  const enabledSocialLinks = getEnabledSocialLinks();

  return (
    <footer className="bg-primary text-primary-foreground py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          {/* Logo and Copyright */}
          <div className="flex items-center space-x-4">
            <span className="font-bold">{siteConfig.organizationName}</span>
            <span className="text-primary-foreground/60">|</span>
            <span className="text-sm text-primary-foreground/80">
              © {currentYear} All rights reserved
            </span>
          </div>

          {/* Quick Links */}
          <div className="flex items-center space-x-6 text-sm">
            <Link to="/about-us" className="text-primary-foreground/80 hover:text-heritage transition-colors">
              About
            </Link>
            <Link to="/contact" className="text-primary-foreground/80 hover:text-heritage transition-colors">
              Contact
            </Link>
            <Link to="/privacy" className="text-primary-foreground/80 hover:text-heritage transition-colors">
              Privacy
            </Link>
          </div>

          {/* Social Icons */}
          {footerConfig.showSocialLinks && enabledSocialLinks.length > 0 && (
            <div className="flex space-x-3">
              {enabledSocialLinks.map((link) => {
                const IconComponent = socialIcons[link.platform];
                return (
                  <a 
                    key={link.platform}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-primary-foreground/10 hover:bg-heritage/20 rounded-full flex items-center justify-center transition-colors"
                    aria-label={`Follow us on ${link.platform}`}
                  >
                    <IconComponent className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default FooterSimple;




